import React, { FormEvent, useEffect, useMemo, useReducer, useState } from 'react';
import InputContainer from './InputContainer';
import { createUseStyles } from 'react-jss';
import {
    HandleChangeVisible,
    IFormField,
    IFormStructure,
    IMedia,
    SelectValue
} from '../schema/interface';
import InputContext, { InputContextValues } from '../context/InputContext';
import NonDataFieldController from './NonDataFieldController';
import { theme } from '../theme';
import FormStructureContext from '../context/FormStructureContext';
import reducer, { SET_FIELDS } from '../hooks/reducer';
import useDifferFormEntry from '../hooks/useDifferFormEntry';
import faMessages from '../lang/fa';
import messages from '../lang/en';

const styles = createUseStyles({
    formContainer: {
        direction: 'ltr',
        textAlign: 'left',
        boxSizing: 'border-box',
        color: theme.color,
        fontSize: 'inherit',
        paddingTop: '24px'
    },
    rtlFormContainer: {
        direction: 'rtl',
        textAlign: 'right',
        boxSizing: 'border-box',
        color: theme.color,
        fontSize: 'inherit',
        paddingTop: '24px'
    },
    overallInfo: {
        marginBottom: '2em'
    },
    title: {
        margin: '0.5em 0',
        fontSize: '1.5em'
    },
    description: {
        margin: '0'
    },
    submitButton: {
        minWidth: '74px',
        padding: '9px 32px',
        height: '40px',
        borderRadius: '6px',
        backgroundColor: '#509ffa',
        fontFamily: 'inherit',
        color: '#ffffff',
        border: 'none',
        fontSize: '16px',
        '&:hover': {
            cursor: 'pointer',
            backgroundColor: '#125694'
        },
        '&:disabled': {
            opacity: 0.5,
            cursor: 'not-allowed',
            backgroundColor: '#b0b0b0'
        }
    },
    cancelButton: {
        color: '#ee384e',
        background: '#fff',
        width: '96px',
        height: '40px',
        border: '2px solid #ee384e',
        borderRadius: '6px',
        marginInlineEnd: '8px',
        '&:hover': {
            cursor: 'pointer',
            backgroundColor: '#ee384e',
            color: '#fff'
        }
    },
    submitContainer: {
        textAlign: ({ rtl }) => (rtl ? 'left' : 'right'),
        margin: '13px 0',
        '& > button': {
            fontSize: '14px',
            fontWeight: 'bold'
        }
    },
    fieldContainer: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr 1fr',
        columnGap: '15px',
        rowGap: '5px',
        alignItems: 'flex-end'
    },
    '@media (min-width: 1672px)': {
        fieldContainer: {
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))'
        }
    },
    '@media (max-width: 1025px)': {
        fieldContainer: {
            gridTemplateColumns: 'repeat(auto-fit, minmax(251px, 1fr))'
        }
    },
    '@media (max-width: 769px)': {
        fieldContainer: {
            gridTemplateColumns: '1fr 1fr'
        }
    },
    '@media (max-width: 555px)': {
        fieldContainer: {
            gridTemplateColumns: '1fr'
        }
    },
    errorWrapper: {
        minHeight: '50px',
        background: 'rgb(255,240,240)',
        color: 'red',
        padding: '12px',
        borderRadius: '4px',
        marginBottom: '12px',
        position: 'relative',
        whiteSpace: 'pre-line',
        lineHeight: '1.5'
    },
    closeIcon: {
        position: 'absolute',
        right: '8px',
        top: '0',
        color: 'red',
        fontSize: '21px',
        fontWeight: 'bold',
        cursor: 'pointer'
    }
});
type FormProps = {
    formStructure: IFormStructure;
    handleSubmit: Function;
    uploadMedia?: Function;
    prevFormValues: any;
    submitted: boolean;
    medias: Record<number, IMedia>;
    handleChangeVisible?: HandleChangeVisible;
    noAccess?: boolean;
    formEntryHasChanged?: Function;
    handleCancel?: React.MouseEventHandler<HTMLButtonElement>;
    lang?: string;
};
const Form = ({
    formStructure,
    handleSubmit,
    prevFormValues,
    submitted,
    uploadMedia,
    medias = {},
    handleChangeVisible,
    noAccess,
    formEntryHasChanged,
    handleCancel,
    lang
}: FormProps) => {
    const classes = styles({ rtl: lang === 'fa' });
    const [{ formValues }, dispatch] = useReducer(reducer, { formValues: {} });
    const inputContextProvider = useMemo(() => ({ dispatch }), [dispatch]);
    const inputContextValuesProvider = useMemo(() => ({ formValues }), [formValues]);
    const [fetchedFormStructure, setFormStructure] = useState<IFormStructure>();
    const [errorMessage, setErrorMessage] = useState<string>(null);
    const [media, setMedia] = useState<IMedia | string>(null);
    useDifferFormEntry(prevFormValues, formValues, formEntryHasChanged);

    const formContextProvider = useMemo(
        () => ({
            fetchedFormStructure,
            setFormStructure,
            errorMessage,
            setErrorMessage,
            media,
            setMedia
        }),
        [fetchedFormStructure, setFormStructure, errorMessage, setErrorMessage, media, setMedia]
    );

    useEffect(() => {
        const interval = setInterval(() => {
            if (errorMessage) {
                setErrorMessage(null);
            }
        }, 30000);
        return () => clearInterval(interval);
    }, [errorMessage]);

    useEffect(() => {
        if (formStructure) {
            setFormStructure(JSON.parse(JSON.stringify(formStructure)));
        }
    }, [formStructure]);
    // remove non data types from created form entry to solve problem of validating of the created form entries
    const reformValues = (prevFormValues: Object) => {
        const formValues = JSON.parse(JSON.stringify(prevFormValues || {}));
        formStructure.fields?.forEach((val) => {
            if (val && val.data_type === 'none') {
                if (formValues[val.name]) {
                    delete formValues[val.name];
                }
            }
        });
        return formValues;
    };
    const defaultValueOfFormValues = (field: IFormField) => {
        const selectValue: SelectValue = { choices: [], others: [] };
        return field?.data_type === 'select' ? selectValue : '';
    };

    // create empty template for creating form entry
    const createEmptyTemplateFormValue = (fields: IFormField[]) => {
        let newObject = {};
        fields?.forEach((val) => {
            if (val && val.data_type !== 'none' && val.data_type !== 'group') {
                newObject = { ...newObject, [val.name]: defaultValueOfFormValues(val) };
            }
        });
        return newObject;
    };
    useEffect(() => {
        if (prevFormValues) {
            dispatch({
                type: SET_FIELDS,
                payload: reformValues(prevFormValues)
            });
            // setFormValues();
        } else {
            dispatch({
                type: SET_FIELDS,
                payload: createEmptyTemplateFormValue(formStructure.fields)
            });
            // setFormValues(newObject);
        }
    }, [prevFormValues]);

    useEffect(() => {
        const media = Object.keys(medias);
        if (media?.length) {
            media?.forEach((key: string) => {
                const uploadLink = medias?.[key];
                if (uploadLink) {
                    setMedia({ ...uploadLink, name: key });
                }
            });
        }
    }, [medias]);

    // give empty value to the fields that
    // are not in the formEntry and create new formValues
    const completeFormValues = () => {
        const values = Object.keys(formValues);
        const findExistFields = values?.length
            ? formStructure?.fields?.filter((item) => values.find((key) => item.name === key))
            : null;
        const findNotExistFields = values?.length
            ? formStructure?.fields?.filter((item) => !values.find((key) => item.name === key))
            : null;
        let existFromValues = {};
        findExistFields?.forEach(
            (item: IFormField) =>
                (existFromValues = {
                    ...existFromValues,
                    [item.name]: item?.presentation?.invisible
                        ? defaultValueOfFormValues(item)
                        : formValues[item.name]
                })
        );
        const createEmptyValueOfForm =
            findNotExistFields?.length && createEmptyTemplateFormValue(findNotExistFields);
        return { ...existFromValues, ...createEmptyValueOfForm };
    };

    const handleFormSubmission = (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        // const formattedValues = { ...formValues };
        const values = completeFormValues();
        handleSubmit(values);
        setMedia('isSubmitted');
    };
    if (!formStructure) {
        return null;
    }
    return (
        <FormStructureContext.Provider value={formContextProvider}>
            <InputContext.Provider value={inputContextProvider}>
                <InputContextValues.Provider value={inputContextValuesProvider}>
                    <form
                        className={
                            formStructure?.presentation?.rtl
                                ? classes.rtlFormContainer
                                : classes.formContainer
                        }
                        onSubmit={(e) => {
                            handleFormSubmission(e);
                        }}
                    >
                        {/* <div className={classes.overallInfo}>
                            <h3 className={classes.title}>{formStructure.title}</h3>
                            {formStructure.description && (
                                <p className={classes.description}>{formStructure.description}</p>
                            )}
                        </div> */}
                        {errorMessage ? (
                            <div className={classes.errorWrapper}>
                                {errorMessage}
                                <div
                                    className={classes.closeIcon}
                                    onClick={() => setErrorMessage(null)}
                                >
                                    &times;
                                </div>
                            </div>
                        ) : null}

                        <div className={classes.fieldContainer}>
                            {formStructure.fields
                                ?.filter((item) => !item?.parent)
                                ?.sort((a, b) => a.order - b.order)
                                ?.map((field, index) => {
                                    if (field.data_type === 'none') {
                                        return (
                                            <NonDataFieldController
                                                field={field}
                                                key={field.name}
                                            />
                                        );
                                    }
                                    return (
                                        <InputContainer
                                            field={field}
                                            prevField={
                                                index > 0 ? formStructure.fields[index - 1] : null
                                            }
                                            uploadMedia={(e) => uploadMedia?.(e)}
                                            key={field.name}
                                            value={(formValues && formValues[field.name]) || ''}
                                            formValues={formValues}
                                            handleChangeVisible={handleChangeVisible}
                                            noAccess={noAccess}
                                            rtl={formStructure?.presentation?.rtl}
                                        />
                                    );
                                })}
                        </div>
                        <div className={classes.submitContainer}>
                            <button
                                type="button"
                                className={classes.cancelButton}
                                onClick={handleCancel}
                            >
                                {lang === 'fa' ? faMessages.cancel : messages.cancel}
                            </button>
                            <button
                                className={classes.submitButton}
                                type="submit"
                                disabled={submitted}
                            >
                                {lang === 'fa' ? faMessages.submitForm : messages.submitForm}
                            </button>
                        </div>
                    </form>
                </InputContextValues.Provider>
            </InputContext.Provider>
        </FormStructureContext.Provider>
    );
};

export default Form;
